import { useAppSelector } from "hooks/storeHooks"
import { FC, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"
import {
  Divider,
  MainNav,
  NavBrand,
  NavFooter,
  NavLink,
  NavSection,
  NavSections,
} from "@strapi/design-system"
import { Paint, Paragraph, Question, ShoppingCart, Star, User, Eye } from "@strapi/icons"
import { NavSectionCategories } from "./NavSectionCategories"
import { INavSections } from "./types"
import { selectUserInfo } from "store/user/userSlice"
import { IntegrationType } from "types"
import { LanguagePicker } from "./LanguageSelector"
import { DemoUserButton } from "./DemoUserButton"

const getTranslatedNavSections = (
  t: (key: string) => string,
  integrationType: IntegrationType
): INavSections[] => [
  {
    title: t("nav.uiCustomization"),
    groups: ["Merchant"],
    items: [
      {
        to: "/ui-customization",
        activePaths: ["/ui-customization"],
        label: t("nav.widgetUiSettings"),
        icon: <Paint />,
      },
      {
        to: "/analytics",
        activePaths: ["/analytics"],
        label: t("nav.analytics"),
        icon: <Eye />,
      },
    ],
  },
  {
    title: t("nav.admin"),
    groups: ["Admin"],
    items: [
      {
        to: "/analytics",
        activePaths: ["/analytics"],
        label: t("nav.analytics"),
        icon: <Eye />,
      },
      {
        to: "/brands",
        activePaths: ["/brands"],
        label: t("nav.brands"),
        icon: <Star />,
      },
      {
        to: "/merchants",
        activePaths: ["/merchants"],
        label: t("nav.merchants"),
        icon: <ShoppingCart />,
      },
      {
        to: "/users",
        activePaths: ["/users"],
        label: t("nav.users"),
        icon: <User />,
      },
      {
        to: "/translates",
        activePaths: ["/translates"],
        label: t("nav.translates"),
        icon: <Paragraph />,
      },
    ],
  },
  {
    title: t("nav.info"),
    items: [
      {
        to:
          integrationType === IntegrationType.TIENDANUBE
            ? "https://www.banuba.com/es/support-tint"
            : "https://www.banuba.com/support-tint",
        activePaths: [""],
        label: t("nav.support"),
        icon: <Question />,
        target: "_blank",
      },
    ],
  },
]

export const Navbar: FC = () => {
  const location = useLocation()
  const user = useAppSelector((state) => state.user.userInfo)
  const { t } = useTranslation()

  const { integrationType } = useAppSelector(selectUserInfo)
  const navSectionsFiltered = useMemo(() => {
    const navSections = getTranslatedNavSections(t, integrationType)
    return navSections.filter(({ groups }) => {
      if (user.groups?.length && groups && groups.length) return groups.includes(user.groups[0])
      else return true
    })
  }, [t, user.groups, integrationType])

  return (
    <MainNav condensed={false} style={{ width: "inherit" }}>
      <NavBrand
        workplace={t("nav.workplace")}
        title={t("nav.dashboardTitle")}
        icon={<img src={"/tint-logo.svg"} alt={t("nav.logoAlt")} />}
      />
      <Divider />
      <NavSections>
        <NavSectionCategories />
        {navSectionsFiltered.map(({ title, items }) => (
          <NavSection label={title} key={title}>
            {items.map((link) => (
              <NavLink
                target={link.target || "_self"}
                to={link.to}
                icon={link.icon}
                key={link.to + title + link.label}
                className={
                  link.activePaths.some((el: string) => location.pathname === el) ? "active" : ""
                }
              >
                {link.label}
              </NavLink>
            ))}
          </NavSection>
        ))}
      </NavSections>
      <NavFooter>
        <div className="flex flex-col gap-2 w-full px-3">
          <LanguagePicker />
          <DemoUserButton />
        </div>
      </NavFooter>
    </MainNav>
  )
}
